import React, { useEffect, useState } from 'react';
import { Box, Chip, Grid, IconButton, Paper, Typography } from '@material-ui/core';
import { Add, DeleteSweep } from '@material-ui/icons';
import { BadgeButton } from './Buttons';
import { moneyFormat } from 'src/utils/Common';

export function DefectList({
    data,
    currency,
    tanentCode,
    onAddLabour,
    onAddMaterial,
    onDeleteLabour,
    onDeleteMaterial
}) {
    const [filters, setFilters] = useState({});

    const handleFilterChange = (defectId, filter) => {
        setFilters(prev => ({ ...prev, [defectId]: filter }));
    };

    useEffect(() => {
        const newFilters = { ...filters };

        data.forEach(defect => {
            const current = filters[defect.DefectId] || 'All';
            const hasLabours = defect.labours?.length > 0;
            const hasMaterials = defect.materials?.length > 0;

            if (current === 'Labours' && !hasLabours) {
                newFilters[defect.DefectId] = hasMaterials ? 'Materials' : 'All';
            }

            if (current === 'Materials' && !hasMaterials) {
                newFilters[defect.DefectId] = hasLabours ? 'Labours' : 'All';
            }
        });

        setFilters(newFilters);
    }, [data]);

    return (
        <Grid container direction="column" spacing={2}>
            {data.map(defect => {
                const hasLabours = defect.labours?.length > 0;
                const hasMaterials = defect.materials?.length > 0;
                const activeFilter = filters[defect.DefectId] || 'All';

                const filteredLabours =
                    (activeFilter === 'All' || activeFilter === 'Labours') && hasLabours ? defect.labours : [];

                const filteredMaterials =
                    (activeFilter === 'All' || activeFilter === 'Materials') && hasMaterials ? defect.materials : [];

                return (
                    <Grid item key={defect.DefectId}>
                        <Paper variant="elevation" elevation={2}>
                            <Box px={3} py={2}>
                                <Grid container justifyContent="space-between" alignItems="center">
                                    <Grid item>
                                        <Grid container direction="column" spacing={1}>
                                            <Grid item>
                                                <Typography variant="h6">
                                                    {defect.userCode} - {defect.DefectDesc}
                                                </Typography>
                                            </Grid>
                                            <Grid container item alignItems="center" spacing={1}>
                                                <Grid item>
                                                    <Chip
                                                        size="small"
                                                        label="All"
                                                        variant={activeFilter === 'All' ? 'default' : 'outlined'}
                                                        color={activeFilter === 'All' ? 'primary' : 'default'}
                                                        onClick={() => handleFilterChange(defect.DefectId, 'All')}
                                                    />
                                                </Grid>

                                                {hasLabours && (
                                                    <Grid item>
                                                        <Chip
                                                            size="small"
                                                            label="Labours"
                                                            variant={
                                                                activeFilter === 'Labours' ? 'default' : 'outlined'
                                                            }
                                                            color={activeFilter === 'Labours' ? 'primary' : 'default'}
                                                            onClick={() =>
                                                                handleFilterChange(defect.DefectId, 'Labours')
                                                            }
                                                        />
                                                    </Grid>
                                                )}

                                                {hasMaterials && (
                                                    <Grid item>
                                                        <Chip
                                                            size="small"
                                                            label="Materials"
                                                            variant={
                                                                activeFilter === 'Materials' ? 'default' : 'outlined'
                                                            }
                                                            color={activeFilter === 'Materials' ? 'primary' : 'default'}
                                                            onClick={() =>
                                                                handleFilterChange(defect.DefectId, 'Materials')
                                                            }
                                                        />
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item>
                                        <Grid container spacing={1}>
                                            <Grid item>
                                                <BadgeButton
                                                    main="L"
                                                    content={<Add />}
                                                    onClick={() => onAddLabour(defect)}
                                                    size={2}
                                                />
                                            </Grid>
                                            <Grid item>
                                                <BadgeButton
                                                    main="M"
                                                    content={<Add />}
                                                    onClick={() => onAddMaterial(defect)}
                                                    size={2}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Box>
                            <Box px={2} py={1}>
                                {filteredLabours.map(labour => (
                                    <DefectItem
                                        key={labour.LabourId}
                                        id={labour.LabourId}
                                        tag={{ label: 'Labour', variant: 'outlined' }}
                                        title={labour.LabourDesc}
                                        price={labour.LabourPrice}
                                        currency={currency}
                                        tanentCode={tanentCode}
                                        onDelete={() => onDeleteLabour(labour)}
                                    />
                                ))}
                                {filteredMaterials.map(material => (
                                    <DefectItem
                                        key={material.MaterialId}
                                        id={material.MaterialId}
                                        tag={{ label: 'Material' }}
                                        title={material.MaterialDesc}
                                        price={material.MaterialPrice}
                                        count={material.Count}
                                        currency={currency}
                                        tanentCode={tanentCode}
                                        onDelete={() => onDeleteMaterial(material)}
                                    />
                                ))}
                            </Box>
                        </Paper>
                    </Grid>
                );
            })}
        </Grid>
    );
}

function DefectItem({ id, tag, title, price, currency, tanentCode, count, onDelete }) {
    return (
        <Paper>
            <Box px={2} py={1} mb={1}>
                <Grid container direction="row" wrap="nowrap" justifyContent="space-between">
                    <Grid container item direction="column" justifyContent="center" spacing={1}>
                        <Grid container item alignItems="center" spacing={1}>
                            <Grid item>
                                <Chip size="small" {...tag} />
                            </Grid>
                            {count && (
                                <Grid item>
                                    <Typography variant="caption"> {count} piece</Typography>
                                </Grid>
                            )}
                        </Grid>
                        <Grid item>
                            <Typography variant="body1">
                                {id} - {title}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant="body2">
                                {moneyFormat(price, tanentCode, currency)} {currency}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <IconButton onClick={onDelete}>
                            <DeleteSweep color="action" />
                        </IconButton>
                    </Grid>
                </Grid>
            </Box>
        </Paper>
    );
}
